import { useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { isNilOrEmptyString, isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useValidateFieldsPromise from '@gef-ui/form-core/hooks/form/useValidateFieldsPromise';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { MPU_NOTIF_TASKCOPY_SUP } from 'features/common/acl/constants';
import { updateNotifEmailByNotificationId } from 'features/views/settings/actions';
import { emailNotificationSetIds, notificationSetIds } from 'features/views/settings/constants';
import { FIELDS, SETTINGS_EMAILS_FORM_NAME } from 'features/views/settings/form';
import { getOriginalEmailByNotificationSetIds } from 'features/views/settings/selectors';
import { generateEmailPermission, getSwitchDescription } from 'features/views/settings/utils';
import { getContextUser } from 'features/views/userContext/selectors';
import { getCodelistByName } from 'utils';
import {
	hasContextUserPermissionByIdsAndIncludedValues,
	hasPermissionByIdsAndIncludedValuesWithAllKeywordCheck,
} from 'utils/permissionResolvers';

import SettingsSwitchWithModalLink from './SettingsSwitchWithModalLink';

const { MY_TASKS_AND_INFOS, SUBS_TASKS_AND_INFOS } = notificationSetIds;

export const TasksAndInformationByEmail = () => {
	const dispatch = useDispatch();
	const next = useControlNext(SETTINGS_EMAILS_FORM_NAME);
	const formData = useFormData();
	const formIsInitiated = isObject(formData);
	const validateFields = useValidateFieldsPromise();

	const contextUser = useSelector(getContextUser);

	const originalEmail = useSelector((state) =>
		getOriginalEmailByNotificationSetIds(state, [MY_TASKS_AND_INFOS, SUBS_TASKS_AND_INFOS]),
	);

	useEffect(() => {
		if (formIsInitiated) {
			next(setValue({ fieldName: FIELDS.tasksAndInformationByEmailEmail, value: originalEmail }));
		}
	}, [next, formIsInitiated, originalEmail]);

	const notificationsSetCodelist = useSelector(getCodelistByName(codelists.notificationsSet));

	const emailPermission = generateEmailPermission(MPU_NOTIF_TASKCOPY_SUP);
	const userShowEmailPermission = useSelector((state) =>
		hasPermissionByIdsAndIncludedValuesWithAllKeywordCheck(state)(emailPermission),
	);

	const contextUserShowEmailPermission = useSelector((state) =>
		hasContextUserPermissionByIdsAndIncludedValues(state)(emailPermission),
	);

	const hasShowEmailPermission = contextUser ? contextUserShowEmailPermission : userShowEmailPermission;

	const handleEmailChange = useCallback(async () => {
		try {
			await validateFields([[FIELDS.tasksAndInformationByEmailEmail]]);
			dispatch(updateNotifEmailByNotificationId(emailNotificationSetIds.TASK_AND_INFOS_EMAIL));
		} catch (error) {
			return;
		}
	}, [dispatch, validateFields]);

	const getSwitchShortDescription = getSwitchDescription(notificationsSetCodelist);

	const settingsHeader = (
		<Stack
			spacing={1}
			direction="row"
			sx={{
				alignItems: 'center',
			}}
		>
			<Typography variant="h5" color="text.primary">
				{getLabelMessage('settings:tasksAndInformationByEmail')}
			</Typography>
			<InfoCircleTooltip text={getLabelMessage('settings:tasksAndInformationByEmailHeaderTooltip')} />
		</Stack>
	);

	return (
		<Grid size={12}>
			<Grid container>
				<Grid size={12}>{settingsHeader}</Grid>
				<Grid size={12}>
					<Grid container>
						<Grid size={12}>
							<SettingsSwitchWithModalLink
								description={getSwitchShortDescription(MY_TASKS_AND_INFOS)}
								notificationSetId={MY_TASKS_AND_INFOS}
							/>
							<SettingsSwitchWithModalLink
								description={getSwitchShortDescription(SUBS_TASKS_AND_INFOS)}
								notificationSetId={SUBS_TASKS_AND_INFOS}
							/>
						</Grid>
					</Grid>
				</Grid>
				{hasShowEmailPermission && (
					<Grid size={12}>
						<Grid container spacing={2} sx={{ mt: 1 }}>
							<Tooltip title={getLabelMessage('tooltips:email')}>
								<Grid size={8}>
									<TextField
										size="small"
										fieldName={FIELDS.tasksAndInformationByEmailEmail}
										label={getLabelMessage('common:email')}
										slotProps={{
											htmlInput: {
												maxLength: MaxLengths.EMAIL,
											},
										}}
									/>
								</Grid>
							</Tooltip>
							<Grid size={4}>
								<Button
									variant="contained"
									onClick={handleEmailChange}
									disabled={isNilOrEmptyString(formData?.tasksAndInformationByEmailEmail)}
									fullWidth
								>
									{getLabelMessage('settings:saveEmail')}
								</Button>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		</Grid>
	);
};

export default TasksAndInformationByEmail;
